import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import PhotoUploader from "../components/PhotoUploader";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";

const STATUS_LABEL = {
  pending: { text: "Pending review", cls: "sb-badge-soft" },
  approved: { text: "Approved", cls: "sb-badge-soft sb-badge-gold" },
  rejected: { text: "Rejected", cls: "sb-badge-soft", extra: "text-danger" },
};

export default function Premium() {
  const { user, loading, refresh } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [upi, setUpi] = useState({ upi_id: "", upi_payee: "" });
  const [selected, setSelected] = useState("premium_month");
  const [screenshot, setScreenshot] = useState("");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (loading) return;
    if (!user || typeof user !== "object") {
      navigate("/login", { replace: true, state: { from: "/premium" } });
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/payments/plans");
        setPlans(data.plans);
        setUpi({ upi_id: data.upi_id, upi_payee: data.upi_payee });
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
  }, []);

  useEffect(() => {
    if (!user || typeof user !== "object") return;
    (async () => {
      try {
        const { data } = await api.get("/payments/mine");
        setHistory(data);
      } catch {
        // ignore — history is optional
      }
    })();
  }, [user]);

  const selectedPlan = plans.find((p) => p.id === selected);

  const upiDeepLink = useMemo(() => {
    if (!upi.upi_id || !selectedPlan) return "";
    const params = new URLSearchParams({
      pa: upi.upi_id,
      pn: upi.upi_payee || "InstaVivah",
      am: String(selectedPlan.amount),
      cu: "INR",
      tn: `Premium ${selectedPlan.id}`,
    });
    return `upi://pay?${params.toString()}`;
  }, [upi, selectedPlan]);

  const submit = async () => {
    if (!selectedPlan) return;
    if (!reference && !screenshot) {
      toast.error("Please add a UPI transaction reference or upload a screenshot.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/payments/request", {
        plan: selected,
        amount: selectedPlan.amount,
        method: "upi",
        upi_reference: reference,
        screenshot_url: screenshot,
        note,
      });
      toast.success("Payment submitted — we'll verify within 24 hours.");
      setScreenshot("");
      setReference("");
      setNote("");
      const { data } = await api.get("/payments/mine");
      setHistory(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    // If admin just approved, membership changes — refresh occasionally
    if (history.some((h) => h.status === "approved") && user?.membership !== "premium") {
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  if (loading || !user || typeof user !== "object") {
    return (
      <div>
        <Navbar />
        <div className="container py-5">
          <div className="sb-card">Loading…</div>
        </div>
      </div>
    );
  }

  const isPremium = user.membership === "premium";

  return (
    <div>
      <Navbar />
      <div className="container py-4 py-lg-5" data-testid="premium-page">
        <div className="mb-4">
          <div className="sb-kicker mb-1">Membership</div>
          <h1 className="h2 mb-1">Upgrade to Premium.</h1>
          <p className="text-muted mb-0">
            Unlock unlimited contact reveals, priority listing, and advanced filters.
          </p>
        </div>

        {isPremium && (
          <div
            className="sb-card mb-4"
            style={{ borderColor: "var(--sb-gold)", background: "linear-gradient(135deg,#fbf3e0,#fff)" }}
            data-testid="premium-active-banner"
          >
            <div className="sb-kicker">Status</div>
            <h3 className="h5 mb-0">You're a Premium member. Thank you 🌸</h3>
          </div>
        )}

        <div className="row g-4">
          {/* Plans */}
          <div className="col-lg-5">
            <div className="sb-card" data-testid="plan-picker">
              <div className="sb-kicker mb-2">Choose a plan</div>
              {plans.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className={"sb-plan-card" + (selected === p.id ? " active" : "")}
                  onClick={() => setSelected(p.id)}
                  data-testid={`plan-${p.id}`}
                >
                  <div>
                    <div className="fw-semibold">{p.label}</div>
                    <div className="small text-muted">{p.days} days of Premium access</div>
                  </div>
                  <div className="sb-plan-price">₹{p.amount.toLocaleString("en-IN")}</div>
                </button>
              ))}

              <div className="sb-divider" />
              <ul className="sb-plan-features">
                <li>Unlimited "Express interest" requests</li>
                <li>Reveal contact once matched</li>
                <li>Priority listing in search results</li>
                <li>Advanced filters (age, income, gothra)</li>
                <li>Unlimited PDF / DOCX exports</li>
              </ul>
            </div>
          </div>

          {/* Payment */}
          <div className="col-lg-7">
            <div className="sb-card" data-testid="payment-panel">
              <div className="sb-kicker mb-2">Pay via UPI</div>
              <div className="row g-3 align-items-center">
                <div className="col-md-5 text-center">
                  <div className="sb-qr-wrap" data-testid="upi-qr">
                    {upiDeepLink ? (
                      <QRCodeSVG
                        value={upiDeepLink}
                        size={180}
                        bgColor="#ffffff"
                        fgColor="#8b1e41"
                        includeMargin
                        level="M"
                      />
                    ) : (
                      <div className="text-muted small">QR unavailable</div>
                    )}
                  </div>
                  <div className="small text-muted mt-2">Scan with any UPI app</div>
                </div>
                <div className="col-md-7">
                  <div className="mb-2 small">
                    <strong>UPI ID:</strong>{" "}
                    <span data-testid="upi-id">{upi.upi_id || "—"}</span>
                  </div>
                  <div className="mb-2 small">
                    <strong>Payee:</strong> {upi.upi_payee || "—"}
                  </div>
                  <div className="mb-3 small">
                    <strong>Amount:</strong>{" "}
                    <span data-testid="plan-amount">
                      ₹{selectedPlan?.amount?.toLocaleString("en-IN") || "—"}
                    </span>
                  </div>
                  <div className="alert alert-warning py-2 small mb-0">
                    Cash payments? Contact us and mention the reference below. Membership
                    is activated manually after we verify — usually within 24 hours.
                  </div>
                </div>
              </div>

              <div className="sb-divider" />

              <h3 className="h6 mb-3">Send us your payment details</h3>

              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">UPI reference / UTR</label>
                  <input
                    className="form-control"
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    placeholder="e.g. 412334567890"
                    data-testid="payment-reference"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Note (optional)</label>
                  <input
                    className="form-control"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Anything we should know"
                    data-testid="payment-note"
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">Payment screenshot</label>
                  <PhotoUploader value={screenshot} onChange={setScreenshot} />
                </div>
              </div>

              <button
                className="btn btn-primary mt-4"
                onClick={submit}
                disabled={submitting}
                data-testid="payment-submit"
              >
                {submitting ? "Submitting…" : "Submit payment for review"}
              </button>
            </div>

            {/* History */}
            <div className="sb-card mt-4" data-testid="payment-history">
              <h3 className="h6 mb-3">Your payment history</h3>
              {history.length === 0 ? (
                <div className="text-muted small">No payments yet.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm align-middle mb-0">
                    <thead>
                      <tr className="text-muted small">
                        <th>Date</th>
                        <th>Plan</th>
                        <th>Amount</th>
                        <th>Reference</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((h) => {
                        const s = STATUS_LABEL[h.status] || STATUS_LABEL.pending;
                        return (
                          <tr key={h.id} data-testid={`history-${h.id}`}>
                            <td className="small">{new Date(h.created_at).toLocaleDateString()}</td>
                            <td className="small">{h.plan}</td>
                            <td className="small">₹{h.amount}</td>
                            <td className="small text-muted">{h.upi_reference || "—"}</td>
                            <td>
                              <span className={`${s.cls} ${s.extra || ""}`}>{s.text}</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
